// src/jsx/components/Support-tickets/SupportTickets.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import WebService from "../../../services/webservice";
import { MessageSquare } from "lucide-react";
import Swal from "sweetalert2";
import "./SupportTickets.css";

interface Ticket {
  ticketId: string;
  userName: string;
  via: string;
  issue: string;
  message: string;
  time: string;
  isUrgent?: boolean;
  status?: string; // ← Added this line (was missing
  unreadStatus: {
    admin: boolean;
    [userId: string]: boolean;
  };
}

const issuesList = [
  "Server Issue",
  "Mood Monitor Page Slow",
  "Update Issue",
  "Login Problem",
  "Billing Query",
  "Account Issue",
  "Order Issue",
  "Payment Issue",
  "Delivery Issue",
];

  const SupportTickets: React.FC = () => {
  const [listFilter, setListFilter] = useState<"Unread" | "Read">("Unread");
  const [breakdownFilter, setBreakdownFilter] = useState<"Unread" | "Read" | "All">("Unread");
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);
  const [displayTickets, setDisplayTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllTickets = async () => {
      try {
        setLoading(true);
        const enc = AesEncrypt({ filter: "All" });
        const { data } = await axios.post(WebService + "api/get-support-tickets", { Enc: enc });

        if (data.response === "S") {
          const decrypted = AesDecrypt(data.data);
          const tickets: Ticket[] = decrypted.tickets || [];

          // ONLY SHOW NON-CLOSED TICKETS
          const activeTickets = tickets.filter(t => t.status !== "Closed");
          setAllTickets(activeTickets);
        }
      } catch (err) {
        console.error("Error fetching tickets:", err);
        Swal.fire("Error", "Failed to load tickets", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchAllTickets();
  }, []);

  useEffect(() => {
    const filtered = allTickets.filter(ticket => {
      if (listFilter === "Unread") return ticket.unreadStatus.admin === false;
      if (listFilter === "Read") return ticket.unreadStatus.admin === true;
      return true;
    });
    setDisplayTickets(filtered);
  }, [listFilter, allTickets]);

  const breakdownTickets = allTickets.filter(ticket => {
    if (breakdownFilter === "All") return true;
    return breakdownFilter === "Unread"
      ? ticket.unreadStatus.admin === false
      : ticket.unreadStatus.admin === true;
  });

  const getIssueCount = (issue: string) =>
    breakdownTickets.filter(t => t.issue === issue).length;

  const markAsRead = async (ticketId: string) => {
    try {
      const enc = AesEncrypt({ ticketId });
      await axios.post(WebService + "api/mark-ticket-read", { Enc: enc });
      setAllTickets(prev =>
        prev.map(t =>
          t.ticketId === ticketId
            ? { ...t, unreadStatus: { ...t.unreadStatus, admin: true } }
            : t
        )
      );
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const closeTicket = async (ticketId: string) => {
    const result = await Swal.fire({
      title: "Close this ticket?",
      text: "This ticket will be closed and moved to history.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Close it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const enc = AesEncrypt({ ticketId });
      const { data } = await axios.post(WebService + "api/close-ticket", { Enc: enc });

      if (data.response === "S") {
        setAllTickets(prev => prev.filter(t => t.ticketId !== ticketId));
        setDisplayTickets(prev => prev.filter(t => t.ticketId !== ticketId));
        Swal.fire("Closed!", "Ticket moved to history.", "success");
      } else {
        Swal.fire("Error", data.message || "Failed to close", "error");
      }
    } catch (err) {
      console.error("Close error:", err);
      Swal.fire("Error", "Failed to close ticket", "error");
    }
  };

  const ViewChat = async (ticket: Ticket) => {
    if (ticket.unreadStatus.admin === false) {
      await markAsRead(ticket.ticketId);
    }
    navigate(`/User-chat/${ticket.ticketId}`, {
      state: {
        title: ticket.issue,
        userName: ticket.userName,
        via: ticket.via,
        date: new Date().toLocaleDateString("en-GB"),
        status: "Open",
        isUrgent: ticket.isUrgent || false,
      },
    });
  };

  return (
    <div className="support-tickets-container">
      <div className="main-content">
        <div className="tickets-list">
          <div className="conversation-header">
            <h2 className="conversation-title">
                  <MessageSquare 
                        size={22} 
                        color="#ea580c" 
                        className="inline-block align-middle icon" 
                        strokeWidth={2.3} 
                      />                  
                    Conversations
                </h2>
              <div className="filter-bar">
              <button className={listFilter === "Unread" ? "active" : ""} onClick={() => setListFilter("Unread")}>
                Unread
              </button>
              <button className={listFilter === "Read" ? "active" : ""} onClick={() => setListFilter("Read")}>
                Read
              </button>
            </div>
          </div>
          <div className="tickets-scrollable">
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>
            ) : displayTickets.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                No {listFilter.toLowerCase()} tickets
              </div>
            ) : (
              displayTickets.map(ticket => (
                <div key={ticket.ticketId} className="ticket-item">
                  <div className="ticket-header">
                    <span className="user-info">{ticket.userName} • {ticket.via}</span>
                    <span className="issue-tag">{ticket.issue}</span>
                    <span className="time">{ticket.time} ...</span>
                  </div>
                  <div className="message">{ticket.message}</div>
                  {ticket.isUrgent && (
                    <div className="urgent-tag">Priority</div>
                  )}
                  <div className="chat-action">
                    <button
                      className="Ticket-close-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeTicket(ticket.ticketId);
                      }}
                    >
                      Close Ticket
                    </button>
                    <button className="chat-button" onClick={() => ViewChat(ticket)}>
                      Chat
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="right-panel">
          {/* <div className="overview-section">
            <h2>Overview</h2>
            <select
              className="status-filter"
              value={breakdownFilter}
              onChange={e => setBreakdownFilter(e.target.value as any)}
            >
              <option value="Unread">Unread</option>
              <option value="Read">Read</option>
              <option value="All">All</option>
            </select>
          </div>
          <div className="breakdownFilter-Portion">
            <div className="breakdownFilter"></div>
          </div> */}
          <div className="overview-section">
            <h2>Overview</h2>
            <select
              className="status-filter"
              value={breakdownFilter}
              onChange={e => setBreakdownFilter(e.target.value as "Unread" | "Read" | "All")}
            >
              <option value="Unread">Unread</option>
              <option value="Read">Read</option>
              <option value="All">All</option>
            </select>
          </div>

          {/* This is the new highlighted filter badge */}
          <div className="breakdownFilter-Portion">
            <div className="breakdownFilter">
              <span className="filter-badge">
                {breakdownFilter === "All" ? "All Tickets" : breakdownFilter}
                <span className="filter-count">
                  {breakdownTickets.length}
                </span>
              </span>
            </div>
          </div>
          <div className="issue-breakdown">
            <h3>Issue Breakdown</h3>
            {issuesList.map(issue => {
              const count = getIssueCount(issue);
              return (
                <div key={issue} className="issue-item">
                  <span className="issue-name">{issue}</span>
                  <div className="bar-container">
                    <div className="green-bar" style={{ width: count > 0 ? "100%" : "0%" }}></div>
                  </div>
                  <span className="count">{count}</span>
                </div>
              );
            })}
          </div>
          <div className="admin-insight">
            <div className="insight-icon">i</div>
            <p>Admin Insight</p>
            <p>Currently viewing all tickets. Server issues seem to be trending.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;